import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Drawer, Form, Popconfirm, Row, Space, Typography, message, theme } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE_URL } from "../../config/serverApiConfig";
import { isQalibDay, isTaqalDay, qalibDateFromSurgery, taqalDateFromQalib, weekdayKu } from "../../utils/surgeryAppointments";
import { AptDatePicker, AptTimePicker } from "./AptPickers";
import "./styles.css";

const { Title, Text } = Typography;

function nowClock() {
  const now = dayjs();
  const rounded = Math.round(now.minute() / 5) * 5;
  return now.minute(0).second(0).add(rounded, "minute").format("HH:mm");
}

function clockFrom(time) {
  return time ? String(time).slice(0, 5) : nowClock();
}

function ymd(value) {
  return value ? value.format("YYYY-MM-DD") : null;
}

function visitKind(item) {
  if (item?.qalibDate && item?.taqalDate) return "both";
  if (item?.taqalDate) return "taqal";
  return "qalib";
}

function latestBySurgery(items) {
  return [...(items || [])].sort((a, b) => dayjs(b.surgeryDate || 0).valueOf() - dayjs(a.surgeryDate || 0).valueOf())[0] || null;
}

function pairForKind(items, nextKind) {
  const open = (items || []).filter((item) => {
    if (!item.surgeryDate) return false;
    if (nextKind === "qalib") return !item.qalibDate;
    if (nextKind === "taqal") return !item.taqalDate;
    return false;
  });
  return latestBySurgery(open);
}

function dateStr(value) {
  if (!value) return null;
  if (typeof value === "string") return value.slice(0, 10);
  return ymd(value);
}

function formatTime(time) {
  if (!time) return "";
  const parsed = dayjs(String(time).slice(0, 5), "HH:mm");
  return parsed.isValid() ? parsed.format("h:mm A") : String(time).slice(0, 5);
}

function formatDay(date, time) {
  if (!date) return "—";
  const clock = formatTime(time);
  return `${dayjs(date).format("YYYY/MM/DD")} · ${weekdayKu(date)}${clock ? ` · ${clock}` : ""}`;
}

function drawerTitle(editing, kind) {
  if (kind === "taqal") return editing ? "دەستکاری تەقەڵ" : "زیادکردنی تەقەڵ";
  if (kind === "both") return "دەستکاری چاوپێکەوتن";
  return editing ? "دەستکاری قالب" : "زیادکردنی قالب";
}

function AptStep({ label, date, time, tone, extra }) {
  if (!date) return null;
  return (
    <div className={`apt-step apt-step-${tone}`}>
      <div>
        <div className="apt-step-label">{label}</div>
        <div className="apt-step-day">{weekdayKu(date)}</div>
        <div className="apt-step-date">{dayjs(date).format("YYYY/MM/DD")}</div>
      </div>
      {extra || time ? (
        <div className="apt-step-side">
          {extra}
          {time ? <span className="apt-time">{formatTime(time)}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function Appointment({ onTabCount }) {
  const { id } = useParams();
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [kind, setKind] = useState("qalib");
  const [pairing, setPairing] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const filling = useRef(false);
  const surgeryDate = Form.useWatch("surgeryDate", form);
  const qalibDate = Form.useWatch("qalibDate", form);
  const taqalDate = Form.useWatch("taqalDate", form);
  const visitTime = Form.useWatch("visitTime", form);
  const suggestedQalib = useMemo(() => (surgeryDate ? qalibDateFromSurgery(surgeryDate) : null), [surgeryDate]);
  const savedQalibKey = kind === "taqal" && pairing?.qalibDate ? dayjs(pairing.qalibDate).format("YYYY-MM-DD") : "";
  const suggestedTaqal = useMemo(() => {
    const base = savedQalibKey || (suggestedQalib ? dayjs(suggestedQalib).format("YYYY-MM-DD") : "");
    return base ? taqalDateFromQalib(base) : null;
  }, [savedQalibKey, suggestedQalib]);
  const showQalib = kind !== "taqal";
  const showTaqal = kind === "taqal" || kind === "both";
  const knownSurgery = Boolean(pairing && !editing);

  useEffect(() => {
    axios.get(`${API_BASE_URL}appointment/patient/${id}`).then((res) => {
      setData(res.data || []);
    });
    axios.get(`${API_BASE_URL}appointment`).then((res) => {
      setBookings(res.data || []);
    }).catch(() => {
      setBookings([]);
    });
  }, [id]);

  useEffect(() => {
    if (filling.current || !visible || editing) return;
    if (!surgeryDate) {
      form.setFieldsValue({
        qalibDate: showQalib ? null : form.getFieldValue("qalibDate"),
        taqalDate: showTaqal ? null : form.getFieldValue("taqalDate"),
      });
      return;
    }
    const qalib = qalibDateFromSurgery(surgeryDate);
    const taqalBase = pairing?.qalibDate && !showQalib ? dayjs(pairing.qalibDate) : qalib;
    form.setFieldsValue({
      qalibDate: showQalib ? qalib : null,
      taqalDate: showTaqal ? taqalDateFromQalib(taqalBase) : null,
    });
  }, [form, surgeryDate, showQalib, showTaqal, visible, editing, pairing]);

  useEffect(() => {
    if (!visible) return;
    if (kind === "both") {
      form.setFieldsValue({
        qalibTime: form.getFieldValue("qalibTime") || nowClock(),
        taqalTime: form.getFieldValue("taqalTime") || nowClock(),
      });
      return;
    }
    if (!form.getFieldValue("visitTime")) {
      form.setFieldsValue({ visitTime: nowClock() });
    }
  }, [form, visible, kind, qalibDate, taqalDate]);

  const closeForm = () => {
    setVisible(false);
    setEditing(null);
    setPairing(null);
    setKind("qalib");
    form.resetFields();
  };

  const openForm = (nextKind) => {
    const pair = pairForKind(data, nextKind);
    const surgery = pair?.surgeryDate ? dayjs(pair.surgeryDate) : dayjs();
    const clock = nowClock();
    const qalib = pair?.qalibDate ? dayjs(pair.qalibDate) : qalibDateFromSurgery(surgery);
    filling.current = true;
    setEditing(null);
    setPairing(pair);
    setKind(nextKind);
    form.setFieldsValue({
      surgeryDate: surgery,
      qalibDate: nextKind === "qalib" ? qalib : null,
      taqalDate: nextKind === "taqal" ? taqalDateFromQalib(qalib) : null,
      visitTime: clock,
    });
    setVisible(true);
    window.requestAnimationFrame(() => {
      filling.current = false;
    });
  };

  const openEdit = (item, nextKind = visitKind(item)) => {
    filling.current = true;
    setEditing(item);
    setKind(nextKind);
    form.setFieldsValue({
      surgeryDate: item.surgeryDate ? dayjs(item.surgeryDate) : dayjs(),
      qalibDate: item.qalibDate ? dayjs(item.qalibDate) : null,
      taqalDate: item.taqalDate ? dayjs(item.taqalDate) : null,
      visitTime: nextKind === "taqal" ? clockFrom(item.taqalTime) : clockFrom(item.qalibTime),
      qalibTime: clockFrom(item.qalibTime),
      taqalTime: clockFrom(item.taqalTime),
    });
    setVisible(true);
    window.requestAnimationFrame(() => {
      filling.current = false;
    });
  };

  const payload = (values) => {
    const clock = values.visitTime || null;
    const source = editing || pairing;
    if (source && kind !== "both") {
      return {
        surgeryDate: ymd(values.surgeryDate) || dateStr(source.surgeryDate),
        qalibDate: kind === "qalib" ? ymd(values.qalibDate) : dateStr(source.qalibDate),
        taqalDate: kind === "taqal" ? ymd(values.taqalDate) : dateStr(source.taqalDate),
        surgeryTime: null,
        qalibTime: kind === "qalib" ? clock : source.qalibTime || null,
        taqalTime: kind === "taqal" ? clock : source.taqalTime || null,
        patient: { id: Number(id) },
      };
    }
    return {
      surgeryDate: ymd(values.surgeryDate),
      qalibDate: kind === "taqal" ? null : ymd(values.qalibDate),
      taqalDate: kind === "qalib" ? null : ymd(values.taqalDate),
      surgeryTime: null,
      qalibTime: kind === "taqal" ? null : kind === "both" ? values.qalibTime || clock : clock,
      taqalTime: kind === "qalib" ? null : kind === "both" ? values.taqalTime || clock : clock,
      patient: { id: Number(id) },
    };
  };

  const submit = (values) => {
    setConfirmLoading(true);
    const target = editing || pairing;
    const request = target
      ? axios.put(`${API_BASE_URL}appointment/${target.id}`, payload(values))
      : axios.post(`${API_BASE_URL}appointment`, payload(values));
    request
      .then((res) => {
        if (target) {
          setData((prev) => prev.map((item) => (item.id === target.id ? res.data : item)));
          setBookings((prev) => prev.map((item) => (item.id === target.id ? res.data : item)));
          message.success(editing ? "چاوپێکەوتن نوێکرایەوە" : "چاوپێکەوتن تۆمارکرا");
        } else {
          setData((prev) => [res.data, ...prev]);
          setBookings((prev) => [res.data, ...prev]);
          onTabCount?.("appointments", 1);
          message.success("چاوپێکەوتن تۆمارکرا");
        }
        closeForm();
      })
      .catch((err) => {
        if (err.response?.status === 409) {
          return message.error("ئەم کاتە گیراوە");
        }
        message.error(editing ? "نەتوانرا چاوپێکەوتن نوێ بکرێتەوە" : "نەتوانرا چاوپێکەوتن تۆمار بکرێت");
      })
      .finally(() => {
        setConfirmLoading(false);
      });
  };

  const remove = (rec) => {
    axios.delete(`${API_BASE_URL}appointment/${rec.id}`).then(() => {
      setData((prev) => prev.filter((item) => item.id !== rec.id));
      setBookings((prev) => prev.filter((item) => item.id !== rec.id));
      onTabCount?.("appointments", -1);
    });
  };

  return (
    <div>
      <Row className="tab-head" align="middle" style={{ marginTop: 0, borderRadius: 6, padding: 10, backgroundColor: token.colorBgContainer, gap: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          چاوپێکەوتن
        </Title>
        <Space wrap className="apt-add-actions tab-head-action">
          <Button type="primary" onClick={() => openForm("qalib")}>
            زیادکردنی قالب
          </Button>
          <Button type="primary" onClick={() => openForm("taqal")}>
            زیادکردنی تەقەڵ
          </Button>
        </Space>
      </Row>

      <div className="apt-list">
        {data.length === 0 ? <div className="apt-empty">هیچ چاوپێکەوتنێک نییە.</div> : null}
        {data.map((item) => {
          const steps = [
            {
              key: "surgery",
              label: "نەشتەرگەری",
              date: item.surgeryDate,
              time: null,
              tone: "surgery",
              extra: (
                <Popconfirm title="دڵنیایت لە سڕینەوە؟" okText="بەڵێ" cancelText="نەخێر" onConfirm={() => remove(item)}>
                  <Button danger type="text" icon={<DeleteOutlined />} />
                </Popconfirm>
              ),
            },
            {
              key: "qalib",
              label: "لابردنی قالب",
              date: item.qalibDate,
              time: item.qalibTime,
              tone: "qalib",
              extra: <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(item, "qalib")} />,
            },
            {
              key: "taqal",
              label: "لابردنی تەقەڵ",
              date: item.taqalDate,
              time: item.taqalTime,
              tone: "taqal",
              extra: <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(item, "taqal")} />,
            },
          ]
            .filter((step) => step.date);
          const surgery = steps.find((step) => step.key === "surgery");
          const rest = steps
            .filter((step) => step.key !== "surgery")
            .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
          const ordered = surgery ? [surgery, ...rest] : rest;

          return (
            <article key={item.id} className="apt-card">
              {ordered.map((step) => (
                <AptStep key={step.key} label={step.label} date={step.date} time={step.time} tone={step.tone} extra={step.extra} />
              ))}
            </article>
          );
        })}
      </div>

      <Drawer
        className="apt-form-drawer"
        rootClassName="apt-form-drawer"
        title={drawerTitle(editing, kind)}
        placement="bottom"
        height="100%"
        open={visible}
        onClose={closeForm}
        destroyOnClose
        autoFocus={false}
        styles={{
          body: { padding: "16px 16px 48px", overflow: "auto" },
        }}
      >
        <Form form={form} layout="vertical" onFinish={submit}>
          {editing ? (
            <Form.Item name="surgeryDate" hidden rules={[{ required: true, message: "بەرواری نەشتەرگەری پێویستە" }]}>
              <AptDatePicker placeholder="بەرواری نەشتەرگەری" />
            </Form.Item>
          ) : knownSurgery ? (
            <>
              <Form.Item name="surgeryDate" hidden rules={[{ required: true, message: "بەرواری نەشتەرگەری پێویستە" }]}>
                <AptDatePicker placeholder="بەرواری نەشتەرگەری" />
              </Form.Item>
              {surgeryDate ? (
                <div className="apt-known-surgery">
                  <div className="apt-step-label">بەرواری نەشتەرگەری</div>
                  <div>
                    {weekdayKu(surgeryDate)} · {dayjs(surgeryDate).format("YYYY/MM/DD")}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <Form.Item name="surgeryDate" label="بەرواری نەشتەرگەری" rules={[{ required: true, message: "بەرواری نەشتەرگەری پێویستە" }]}>
              <AptDatePicker placeholder="بەرواری نەشتەرگەری" />
            </Form.Item>
          )}

          {surgeryDate && showQalib ? (
            <div style={{ marginBottom: 12, color: "#006d77" }}>قالب: دووشەممە و چوارشەممە.</div>
          ) : null}
          {surgeryDate && showTaqal ? (
            <div style={{ marginBottom: 12, color: "#006d77" }}>تەقەڵ: تەنها یەکشەممە.</div>
          ) : null}

          {showQalib ? (
            <Form.Item name="qalibDate" label="لابردنی قالب" rules={[{ required: true, message: "بەرواری قالب پێویستە" }]}>
              <AptDatePicker
                placeholder="دووشەممە یان چوارشەممە"
                markDay={(current) => {
                  if (suggestedQalib && current.isSame(dayjs(suggestedQalib), "day")) return "need";
                  if (isQalibDay(current)) return "clinic";
                  return "";
                }}
              />
            </Form.Item>
          ) : null}

          {kind === "qalib" ? (
            <Form.Item name="visitTime" label="کات" rules={[{ required: true, message: "کات هەڵبژێرە" }]}>
              <AptTimePicker />
            </Form.Item>
          ) : null}

          {kind === "both" ? (
            <Form.Item name="qalibTime" label="کاتی قالب" rules={[{ required: true, message: "کات هەڵبژێرە" }]}>
              <AptTimePicker />
            </Form.Item>
          ) : null}

          {showTaqal ? (
            <Form.Item name="taqalDate" label="لابردنی تەقەڵ" rules={[{ required: true, message: "بەرواری تەقەڵ پێویستە" }]}>
              <AptDatePicker
                placeholder="یەکشەممە"
                markDay={(current) => {
                  if (suggestedTaqal && current.isSame(dayjs(suggestedTaqal), "day")) return "need";
                  if (isTaqalDay(current)) return "clinic";
                  return "";
                }}
              />
            </Form.Item>
          ) : null}

          {kind === "taqal" ? (
            <Form.Item name="visitTime" label="کات" rules={[{ required: true, message: "کات هەڵبژێرە" }]}>
              <AptTimePicker />
            </Form.Item>
          ) : null}

          {kind === "both" ? (
            <Form.Item name="taqalTime" label="کاتی تەقەڵ" rules={[{ required: true, message: "کات هەڵبژێرە" }]}>
              <AptTimePicker />
            </Form.Item>
          ) : null}

          {kind === "qalib" && qalibDate ? <Text type="secondary">قالب {formatDay(qalibDate, visitTime)}.</Text> : null}
          {kind === "taqal" && taqalDate ? <Text type="secondary">تەقەڵ {formatDay(taqalDate, visitTime)}.</Text> : null}

          <Space style={{ width: "100%", justifyContent: "space-between", marginTop: 16 }}>
            <Button onClick={closeForm}>پاشگەزبوونەوە</Button>
            <Button type="primary" loading={confirmLoading} onClick={() => form.submit()}>
              {editing ? "نوێکردنەوە" : "تۆمارکردن"}
            </Button>
          </Space>
        </Form>
      </Drawer>
    </div>
  );
}

export default Appointment;
